/* eslint-disable react-hooks/set-state-in-effect -- this datetime input intentionally mirrors external value changes into local edit state. */
import React, {useCallback, useEffect, useRef, useState} from "react";
import styled, {css} from "styled-components";
import {DateFormatter} from "../../../../utils/format/DateFormatter";
import {DateUtils} from "../../../../utils/DateUtils";
import {FieldDataType} from "../../../../generated/api/base";
import {IDateFieldFormat} from "../../../modules/settings/modules/field/datatypes/DateFieldSettings";
import {HorizontalAlignment, VerticalAlignment} from "../../../../utils/dom/DomUtils";
import {TimeUtils} from "../../../../utils/TimeUtils";
import {AppUtils} from "../../../../utils/AppUtils";
import {ClickToEditInputStyled, ClickToEditInputV5} from "../clicktoedit/ClickToEditInputV5";
import {IconButtonStyled, IconButtonV5} from "../../interaction/IconButtonV5";
import CalendarIcon from "../../icons/calendar.svg?react";
import ClockThreeIcon from "../../icons/clock-three.svg?react";
import XmarkIcon from "../../icons/xmark.svg?react";
import {baseDistance, ELLIPSIS, fontSize, fontWeight, radius, zIndex} from "../../styles/styles";
import {colorPalette} from "../../styles/colorPalette";
import {PopupV5} from "../../popup/PopupV5";
import {ReactUtils} from "../../../utils/ReactUtils";
import {useClickOutside} from "../../utils";
import {SingleLineInputStyled, SingleLineLabelStyled} from "../../details/datatypes/SingleLineInputV5";
import {TextInputStyled, TextInputWrapperStyled} from "../../details/datatypes/TextInputV5";
import {TimePickerV5} from "./TimePickerV5";
import {DatePickerV5} from "./DatePickerV5";

interface DateTimeInputStyledProps {
	$inline: boolean;
	$isOpen: boolean;
}

interface PickersContainerStyledProps {
	format: IDateTimeInputFormat;
}

interface IDateInputV5Props {
	readonly value: string;
	readonly format: IDateTimeInputFormat;
	readonly disabled?: boolean;
	readonly valueToDate?: string;
	readonly focused?: boolean;
	readonly onInput?: (value: string) => void;
	readonly onChange: (value: string) => void;
	readonly onClick?: () => void;
	readonly inline?: boolean;
}

type IDateTimeInputFormat = "" | "date" | "time" | "datetime" | "daterange" | "timerange";

export const DateTimeInputV5 = (props: IDateInputV5Props) => {
	const {onClick, focused, format, disabled, inline, onChange, onInput} = props;

	const _container = useRef<HTMLDivElement>(null);
	const [isOpen, setIsOpen] = useState<boolean>(!!focused);
	const [editingValue, setEditingValue] = useState<string>(props.value);
	const [valueToDate, setValueToDate] = useState<string>("");

	const closePicker = useCallback(() => {
		AppUtils.disableScrolling(false);

		setIsOpen(false);
	}, []);

	const onOpenPicker = useCallback(
		async (e?: React.MouseEvent) => {
			e?.stopPropagation();
			onClick?.();

			AppUtils.disableScrolling(true);

			setIsOpen(true);

			await TimeUtils.wait(100);
		},
		[onClick],
	);

	useClickOutside([_container], closePicker, "mousedown", undefined, false, isOpen);

	const onInputValue = (value: string) => {
		setEditingValue(value);
		onInput?.(value);
	};

	const onClearClicked = (e: React.MouseEvent) => {
		e.stopPropagation();
		setEditingValue("");
		setValueToDate("");

		onChange(null);
	};

	// This only handles the Popup 'Confirm' button which can't get invalid value (if you put invalid manually, it sets to Date.now()), so no need to check validation
	const onConfirmClicked = () => {
		const {format} = props;
		const from = DateUtils.parse(editingValue);
		let date = "";

		if (format === "daterange" || format === "timerange") {
			const toDate = DateUtils.parse(valueToDate);
			const dateRange = {
				from: from,
				to: toDate,
			};

			date = DateUtils.stringifyDateRange(dateRange);
		} else {
			date = DateUtils.stringify(from);
		}

		onChange(date);
		closePicker();
	};

	const getErrorMessage = (value: string) => {
		if ((format === "date" && !DateUtils.dateValidation(value)) || (format === "time" && !DateUtils.isTimeValid(value))) {
			return "Invalid format";
		}
		return "";
	};

	const saveDateInput = (propsValue: string, format: IDateTimeInputFormat) => {
		if (isOpen) {
			// It's needed because of time only input validation. Eg.: "10:20" would be invalid without date data.
			const placeholderDate = "2021.01.01";
			let inputValue = propsValue;

			switch (format) {
				case "time":
				case "date":
				case "datetime":
					const date = DateUtils.parse(inputValue);

					if (date && inputValue !== editingValue && !disabled) {
						setEditingValue(inputValue);
						onChange(date.toISOString());
						closePicker();
					}

					return;
				case "daterange":
				case "timerange":
					const rangeValue = DateUtils.parseDateRange(inputValue);
					let from = rangeValue.from;
					let to = rangeValue.to;

					if (format === "timerange") {
						from = `${placeholderDate} ${from}`;
						to = `${placeholderDate} ${to}`;
					}

					if (DateUtils.dateValidation(from) && DateUtils.dateValidation(to)) {
						setEditingValue(from);
						setValueToDate(to);
						onChange(inputValue);
					}

					closePicker();
					return;
			}
		}
	};

	const getDisplayedDate = () => {
		const {format, value, valueToDate} = props;

		if (format === "daterange" || format === "timerange") {
			let rangeFormat = IDateFieldFormat.DATE;

			if (format === "daterange") {
				rangeFormat = IDateFieldFormat.DATETIME;
			} else if (format === "timerange") {
				rangeFormat = IDateFieldFormat.TIME;
			}

			if (value === "" || valueToDate === "") {
				return "";
			}

			return `${DateFormatter.format(value, rangeFormat)} - ${DateFormatter.format(valueToDate, rangeFormat)}`;
		} else {
			return DateFormatter.format(value, format);
		}
	};

	useEffect(() => {
		setEditingValue(props.value);
	}, [props.value]);

	return (
		<DateTimeInputStyled
			ref={_container}
			className={ReactUtils.cls("DateTimeInput hbox", {disabled, empty: !props.value, open: isOpen})}
			$inline={inline}
			$isOpen={isOpen}
			onClick={onOpenPicker}
		>
			<ClickToEditInputV5
				value={getDisplayedDate()}
				onChange={(value) => saveDateInput(value, format)}
				dataTypeSettings={FieldDataType.DateTime}
				disabled={disabled}
				getErrorMessage={getErrorMessage}
				inline={inline}
			/>
			{!disabled && (
				<InputIconsStyled
					$inline={inline}
					$hasValue={!!props.value}
				>
					{(!inline || !props.value) && (
						<IconButtonV5
							IconComponent={format === "time" ? ClockThreeIcon : CalendarIcon}
							onClick={onOpenPicker}
							className="dateTimeIcon"
						/>
					)}
					{props.value && (
						<IconButtonV5
							IconComponent={XmarkIcon}
							onClick={onClearClicked}
							className="cancel"
						/>
					)}
				</InputIconsStyled>
			)}
			{isOpen && (
				<DateTimeInputPopupStyled
					parentRef={_container.current}
					label={format === "time" || format === "timerange" ? "Time" : "Calendar"}
					onClose={closePicker}
					closeOnClickOutside={true}
					buttonProps={{
						hideIcon: true,
						label: "Confirm",
						onClick: onConfirmClicked,
					}}
					isSmallPopup={true}
					verticalAlignment={VerticalAlignment.bottomOuter}
					horizontalAlignment={inline ? HorizontalAlignment.center : HorizontalAlignment.right}
					offsetY={10}
					offsetX={0}
					className={ReactUtils.cls({timeOnly: format === "time"})}
				>
					<PickersContainerStyled
						className="pickersContainer"
						format={format}
					>
						{(format === "date" || format === "datetime") && (
							<DatePickerV5
								value={editingValue}
								onChange={onInputValue}
							/>
						)}
						{format === "datetime" && <LineStyled />}
						{(format === "time" || format === "datetime") && (
							<TimePickerV5
								value={DateUtils.parse(editingValue)}
								onChange={onInputValue}
								format={format}
							/>
						)}
					</PickersContainerStyled>
				</DateTimeInputPopupStyled>
			)}
		</DateTimeInputStyled>
	);
};

const InputIconsStyled = styled.div<{$inline: boolean; $hasValue: boolean}>`
	${(props) => {
		if (props.$hasValue) {
			return css`
				position: absolute;
				right: 2px;
				display: flex;

				${IconButtonStyled} {
					&:hover {
						background-color: ${props.$hasValue && props.$inline ? "transparent" : colorPalette.gray.c200Light};
					}
				}
			`;
		} else {
			return css`
				position: absolute;
				right: 0;
				left: 0;
				display: flex;
				justify-content: ${props.$inline ? "center" : "flex-end"};

				${IconButtonStyled} {
					border: none;

					&:hover {
						background-color: transparent;
					}
				}
			`;
		}
	}}

	${IconButtonStyled} {
		border-radius: ${radius.sm};

		${(props) => {
			if (!props.$hasValue) {
				return css`
					width: 24px;
					height: 24px;
				`;
			}
		}}

		svg {
			width: 16px;
			height: 16px;
		}
	}
`;

export const DateTimeInputStyled = styled.div<DateTimeInputStyledProps>`
	position: relative;
	justify-content: space-between;
	width: 100%;
	height: ${({$inline}) => ($inline ? "28px" : "32px")};
	align-items: center;

	${InputIconsStyled} {
		visibility: ${({$isOpen}) => ($isOpen ? "visible" : "hidden")};

		.dateTimeIcon {
			background-color: ${({$isOpen}) => ($isOpen ? colorPalette.primary.c200Light : "transparent")};
			color: ${({$isOpen}) => ($isOpen ? colorPalette.primary.c500Primary : colorPalette.gray.c950)};
		}
	}

	&.empty {
		${ClickToEditInputStyled} {
			width: 0;
		}
	}

	&:hover {
		&:not(.disabled) {
			background-color: ${colorPalette.gray.c200Light};

			${SingleLineLabelStyled}:hover {
				border-color: transparent;
			}
		}

		${InputIconsStyled} {
			visibility: visible;
		}
	}

	.unfocused {
		font-size: ${fontSize.md};
		font-weight: ${({$inline}) => ($inline ? "400" : "300")};
		color: ${colorPalette.gray.c950};

		&.disabled {
			color: ${colorPalette.libraryColors.explosive};
		}
	}

	.dateLabel {
		min-height: 17px;
	}

	${ClickToEditInputStyled} {
		display: flex;
		align-items: center;
		border: none;

		${SingleLineLabelStyled}, ${SingleLineInputStyled} ${TextInputWrapperStyled} ${TextInputStyled} {
			border-color: transparent;
		}
	}

	input {
		border: none;
		height: 32px;

		&:focus {
			background: transparent;
		}
	}

	${(props) => {
		if (props.$inline) {
			return css`
				${ClickToEditInputStyled} {
					height: 100%;
					.unfocused {
						height: 18px;
						min-height: 18px;
						border: none;
						padding: 0;
						text-align: center;
						position: relative;
						left: 4px;
						display: flex;

						&:hover {
							background-color: transparent;
						}

						.SingleLineLabel {
							${ELLIPSIS};
						}
					}
				}
			`;
		}
	}}
`;

const PickersContainerStyled = styled.div<PickersContainerStyledProps>`
	padding: ${baseDistance.md} 0;

	.unfocused {
		box-sizing: border-box;
		padding: 10px;
	}

	.input {
		box-sizing: border-box;
		position: relative;
		padding-right: 30px;
		max-width: 100%;
		${ELLIPSIS};
		font-weight: ${fontWeight.thin};
		font-size: ${fontSize.md};
	}

	&.datetime,
	&.daterange {
		min-width: 375px;
	}

	&.date,
	&.time,
	&.timerange {
		min-width: 305px;
	}

	&.time {
		min-width: 250px;

		.pickerinputs {
			.title {
				width: 40px;
			}

			.row {
				.ClickToEditInputV5 {
					width: 100%;
				}
			}
		}
	}

	.title {
		font-weight: ${fontWeight.thin};
	}
`;

const DateTimeInputPopupStyled = styled(PopupV5)`
	padding: ${baseDistance.md};
	margin: 0;
	z-index: ${zIndex.contextOptions};

	.header {
		height: 32px;
		padding: 0;

		.name {
			margin: 0;
		}

		.cancel {
			margin: 0;
		}
	}

	.body {
		padding: 0;
		gap: 0;
	}

	&.timeOnly {
		padding: ${baseDistance.sm};
		min-width: 241px;

		.header .name {
			display: flex;
		}

		.button {
			margin-top: 0;
		}
	}
`;

const LineStyled = styled.div`
	height: 1px;
	width: 100%;
	background-color: ${colorPalette.gray.c300};
	margin: ${baseDistance.md} 0;
`;
